import React from 'react';
import useSites, { groupSites } from './useSites';
import { siteColour } from '../../_services/sites.service';
import './multiLocation.css';

/**
 * "Locations" control for catalogue create/edit forms. Renders nothing for a
 * single-site account. Value: { mode: 'this' | 'all' | 'choose', salonIds: [] }.
 * `staffOnly` lists only sites the person works at (a self-employed person's
 * own services); otherwise sites they own or manage.
 */
export const LOCATION_SCOPE_THIS = { mode: 'this', salonIds: [] };
export const LOCATION_SCOPE_ALL = { mode: 'all', salonIds: [] };

/** Sites a scope resolves to (ids), given the sites list. */
export function resolveScopeSalonIds(value, sites, activeSalonId) {
    const others = (sites || []).filter((s) => String(s.salonId) !== String(activeSalonId)).map((s) => String(s.salonId));
    if (!value || value.mode === 'this') return [];
    if (value.mode === 'all') return others;
    return (value.salonIds || []).map(String).filter((id) => others.includes(id));
}

export default function LocationScope({ value, onChange, label = 'Available at', help, staffOnly = false }) {
    const { sites: allSites, active, group, inGroup } = useSites();
    if (!inGroup) return null;
    // Only the active site's group: never a salon this person also works at
    // outside it (the server refuses those, and the chip would only error).
    const sites = groupSites({ sites: allSites, group });
    const mine = sites.filter((s) => (staffOnly ? s.hasRecord : (s.role === 'owner' || s.role === 'manager')));
    const others = mine.filter((s) => !active || String(s.salonId) !== String(active.salonId));
    if (!others.length) return null;
    const v = value || LOCATION_SCOPE_THIS;
    // One row of locations. This site is always in and cannot be unticked;
    // the others tick on and off. The value stays { mode, salonIds } for the
    // API: every other site ticked = 'all', none = 'this', some = 'choose'.
    const selected = new Set(resolveScopeSalonIds(v, sites, active && active.salonId));
    const emit = (set) => {
        const ids = others.map((s) => String(s.salonId)).filter((id) => set.has(id));
        if (!ids.length) onChange({ mode: 'this', salonIds: [] });
        else if (ids.length === others.length) onChange({ mode: 'all', salonIds: [] });
        else onChange({ mode: 'choose', salonIds: ids });
    };
    const toggle = (id) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); emit(n); };
    const allOn = others.every((s) => selected.has(String(s.salonId)));
    return (
        <div className="ml-scope">
            <div className="ml-scope-head">
                <span className="ml-scope-label">{label}</span>
                <button type="button" className="ml-scope-link" onClick={() => emit(allOn ? new Set() : new Set(others.map((s) => String(s.salonId))))}>
                    {allOn ? 'Only here' : 'All locations'}
                </button>
            </div>
            <div className="ml-scope-chips">
                {active && (
                    <span className="ml-scope-chip on locked" title="The location you are working in">
                        <span className="ml-dot" style={{ background: siteColour(active.salonId) }} />{active.salonName}<span className="ml-scope-here">here</span>
                    </span>
                )}
                {others.map((s) => {
                    const id = String(s.salonId);
                    const on = selected.has(id);
                    return (
                        <button key={id} type="button" className={`ml-scope-chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => toggle(id)}>
                            <span className="ml-scope-tick" aria-hidden>{on ? '✓' : '+'}</span>
                            <span className="ml-dot" style={{ background: siteColour(id) }} />{s.salonName}
                        </button>
                    );
                })}
            </div>
            {help && <p className="ml-scope-help">{help}</p>}
        </div>
    );
}
